import { Stack, Typography, useTheme } from "@mui/material";
import { KeyRoleMapper } from "../KeyRoleMapper";
import { RowWrapper } from "./RowWrapper";
import { RoleMembers, RoleMembersItem } from "../../../types/apiTypes";
import { useEffect, useState } from "react";
import { MembersUpdateValues } from "../../../types/companyTypes";
import { SortableEvent } from "react-sortablejs";
import { KeyRoles } from "../../../types/enums";
import { GetContactsForRoleBtn, ToTopBtn } from "../../ui/buttons";
import {
  useAppDispatch,
  useAppSelector,
  useStateWithSearchParams,
} from "../../../hooks";
import { setDiscardTableChanges } from "../../../features/companySlice";
import { useGetCampaignCurrentRoleMembersQuery } from "../../../api";
import { EmptyRoleRow } from "./EmptyRoleRow";
import { keyRolesItems } from "../../../types/genereal";
import { RoleTooltip } from "../RoleTooltip";
import { ReuseTooltip } from "../../ui";
import {
  PersonaGroup,
  parseContainerId,
  personaContainerId,
} from "./PersonaGroup";
import { RoleCoverage } from "./RoleCoverage";
import { SortableGroupList } from "./SortableGroupList";
import { MembersTableHeader } from "./CompanyRoleTableHeader";

const emptyTextStyle = {
  fontSize: "12px",
  lineHeight: "18px",
  color: "#464646",
};

type EmptyRoleStateProps = {
  account_id: string;
  role: KeyRoles;
  scraping: KeyRoles | null;
  is_active: boolean;
  isEditor?: boolean;
  currentScraping?: KeyRoles;
};

const EmptyRoleState = (props: EmptyRoleStateProps) => {
  const { account_id, role, scraping, is_active, isEditor, currentScraping } =
    props;

  return (
    <Stack
      className="no-drag"
      sx={{
        flexGrow: 1,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px 0",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
      }}
    >
      {isEditor ? (
        <GetContactsForRoleBtn
          account_id={account_id}
          role={role}
          isLoading={currentScraping?.includes(role) || !!scraping}
          isDisabled={
            [role, "scraping", "update"].some((el) =>
              currentScraping?.includes(el),
            ) ||
            !!scraping ||
            !is_active
          }
        />
      ) : (
        <Stack alignItems="center">
          <Typography sx={emptyTextStyle}>
            No contacts have been added for this role yet.
          </Typography>
          <Typography sx={emptyTextStyle}>
            This can be done by someone with edit access to the campaign.
          </Typography>
        </Stack>
      )}
    </Stack>
  );
};

const NoPersonaMatchHeader = () => {
  const {
    palette: { custom: c },
  } = useTheme();

  return (
    <Stack
      className="no-drag"
      sx={{
        padding: "16px",
        gap: "12px",
        background: c.brandBlueLight,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Inter",
          fontSize: "14px",
          fontWeight: 600,
          lineHeight: "19px",
          letterSpacing: "0.1px",
          textTransform: "capitalize",
          color: c.brandBlue,
        }}
      >
        No Persona
      </Typography>
    </Stack>
  );
};

type Props = RoleMembers & {
  account_id: string;
  isEditMode: boolean;
  updatedValues: { [key: string]: MembersUpdateValues };
  updateHandler: (
    values?: MembersUpdateValues,
    allValues?: { [key: string]: MembersUpdateValues },
  ) => void;
  setCommitteeSelected: React.Dispatch<React.SetStateAction<number>>;
  setClusterSelected: React.Dispatch<React.SetStateAction<number>>;
  currentScraping?: KeyRoles;
};

type SearchParams = {
  sort?: string;
  min?: string;
  max?: string;
};

export const RoleItem = (props: Props) => {
  const {
    id,
    personas,
    unassigned_items,
    total,
    expected_count,
    scraping,
    isEditMode,
    updatedValues,
    updateHandler,
    setCommitteeSelected,
    setClusterSelected,
    account_id,
    is_active,
    status,
    currentScraping,
  } = props;

  const isNotInvolved = id === KeyRoles.other_stakeholder;
  const dispatch = useAppDispatch();
  const isDiscardChanges = useAppSelector(
    (state) => state.company.discardTableChanges,
  );
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const [unassignedList, setUnassignedList] =
    useState<RoleMembersItem[]>(unassigned_items);
  const [page, setPage] = useState<number>(0);
  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const [isScroll, setIsScroll] = useState<boolean>(false);
  const [totalMembers, setTotalMembers] = useState<number>(0);
  const [isShowMore, setIsShowMore] = useState<boolean>(false);
  const canLoadMore = total > totalMembers;

  const membersCount =
    personas.reduce((acc, persona) => acc + persona.items.length, 0) +
    unassigned_items.length;

  useEffect(() => {
    if (isNotInvolved) {
      const el = document.getElementById(id);
      if (el) {
        const handleScroll = () => {
          const { top, height } = el.getBoundingClientRect();
          setShowScrollTop(top <= -200);
          setIsScroll(top <= -height + 4000);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
      }
    }
  }, []);

  useEffect(() => {
    setUnassignedList(unassigned_items);
    setTotalMembers(membersCount);
    setPage(0);
  }, [unassigned_items, personas]);

  const { searchDict } = useStateWithSearchParams<SearchParams>({
    sort: undefined,
    min: undefined,
    max: undefined,
  });

  const { data, isFetching } = useGetCampaignCurrentRoleMembersQuery(
    {
      account_id,
      page,
      per_page: 100,
      role_id: id,
      min_percent: searchDict.min ? Number(searchDict.min) : undefined,
      max_percent: searchDict.max ? Number(searchDict.max) : undefined,
    },
    { refetchOnMountOrArgChange: true, skip: !isNotInvolved || page === 0 },
  );

  useEffect(() => {
    if (isScroll && isShowMore && canLoadMore && !isFetching) {
      setPage((prev) => prev + 1);
      setIsScroll(false);
    }
  }, [isScroll]);

  useEffect(() => {
    if (data && isNotInvolved && page > 0) {
      setUnassignedList((prev) => [...prev, ...data.items]);
      setTotalMembers((prev) => prev + data.items.length);
    }
  }, [data]);

  useEffect(() => {
    if (isDiscardChanges) {
      setUnassignedList(unassigned_items);
      setTotalMembers(membersCount);
      dispatch(setDiscardTableChanges(false));
    }
  }, [isDiscardChanges]);

  const onAdd = (e: SortableEvent) => {
    const member_id = e.item.getAttribute("data-id");
    if (!member_id || e.newIndex === undefined) return;

    const { role, persona_id } = parseContainerId(e.to.id);
    const values = { ...updatedValues };

    if (!updatedValues[member_id]) {
      values[member_id] = {
        member_id,
        role,
        persona_id,
        custom_index: e.newIndex,
        original_index: e.oldIndex,
      };
    }

    const updatedMembers: { [key: string]: MembersUpdateValues } =
      Object.values(values).reduce(
        (acc: { [key: string]: MembersUpdateValues }, el) => {
          if (el.member_id === member_id) {
            acc[el.member_id] = {
              ...el,
              custom_index: e.newIndex,
              role,
              persona_id,
            };
            return acc;
          }

          const elContainer = personaContainerId(el.role, el.persona_id);

          if (e.from.id === elContainer) {
            acc[el.member_id] = {
              ...el,
              custom_index:
                el.custom_index! > e.oldIndex!
                  ? el.custom_index! - 1
                  : el.custom_index!,
            };
            return acc;
          }

          if (e.to.id === elContainer) {
            acc[el.member_id] = {
              ...el,
              custom_index:
                el.custom_index! >= e.newIndex!
                  ? el.custom_index! + 1
                  : el.custom_index!,
            };
            return acc;
          }

          acc[el.member_id] = el;
          return acc;
        },
        {},
      );

    updateHandler(undefined, updatedMembers);
  };

  const onUpdate = (e: SortableEvent) => {
    const member_id = e.item.getAttribute("data-id");
    if (!member_id || e.newIndex === e.oldIndex || e.newIndex === undefined)
      return;

    const { role, persona_id } = parseContainerId(e.from.id);
    const values = { ...updatedValues };

    if (!updatedValues[member_id]) {
      values[member_id] = {
        member_id,
        role,
        persona_id,
        custom_index: e.newIndex,
        original_index: e.oldIndex,
      };
    }

    const updatedMembers: { [key: string]: MembersUpdateValues } =
      Object.values(values).reduce(
        (acc: { [key: string]: MembersUpdateValues }, el) => {
          if (el.member_id === member_id) {
            acc[el.member_id] = { ...el, custom_index: e.newIndex };
            return acc;
          }

          const elContainer = personaContainerId(el.role, el.persona_id);

          if (e.from.id === elContainer) {
            acc[el.member_id] = {
              ...el,
              custom_index:
                el.custom_index! >= e.newIndex! &&
                e.oldIndex! >= el.custom_index!
                  ? el.custom_index! + 1
                  : el.custom_index! <= e.newIndex! &&
                      e.oldIndex! <= el.custom_index!
                    ? el.custom_index! - 1
                    : el.custom_index!,
            };
            return acc;
          }

          acc[el.member_id] = el;
          return acc;
        },
        {},
      );

    updateHandler(undefined, updatedMembers);
  };

  return (
    <RowWrapper>
      <Stack
        sx={{
          width: "193px",
          flexShrink: 0,
          padding: "12px 0",
          gap: "8px",
          alignSelf: "stretch",
        }}
      >
        <ReuseTooltip
          text={
            <RoleTooltip
              tooltip={keyRolesItems[id].tooltip}
              activeStatus={status}
            />
          }
          hideChildren={false}
        >
          <KeyRoleMapper item={id} roleStatus={status} />
        </ReuseTooltip>

        <RoleCoverage
          personas={personas}
          unassignedCount={unassignedList.length}
          expectedCount={expected_count}
        />
      </Stack>

      <Stack
        data-role={id}
        sx={{
          padding: "12px 8px 12px 16px",
          gap: "8px",
          flex: "1 0 0",
          minWidth: 0,
        }}
      >
        {personas.length === 0 && membersCount === 0 && !isEditMode ? (
          <EmptyRoleState
            account_id={account_id}
            role={id}
            scraping={scraping}
            is_active={is_active}
            isEditor={isEditor}
            currentScraping={currentScraping}
          />
        ) : (
          <>
            {personas.map((persona) => (
              <PersonaGroup
                key={persona.id}
                account_id={account_id}
                roleId={id}
                persona={persona}
                isEditMode={isEditMode}
                isDiscardChanges={isDiscardChanges}
                scraping={scraping}
                is_active={is_active}
                currentScraping={currentScraping}
                updatedValues={updatedValues}
                updateHandler={updateHandler}
                setCommitteeSelected={setCommitteeSelected}
                setClusterSelected={setClusterSelected}
                onAdd={onAdd}
                onUpdate={onUpdate}
              />
            ))}

            {(unassignedList.length > 0 || isNotInvolved) && (
              <Stack
                sx={{
                  alignSelf: "stretch",
                  borderRadius: "8px",
                  border: "1px solid var(--border-card, #DEDEDE)",
                  background: "var(--surface-card-default, #FFF)",
                  overflow: "hidden",
                }}
              >
                <NoPersonaMatchHeader />
                {unassignedList.length > 0 && (
                  <Stack
                    className="no-drag"
                    sx={{ padding: "8px 16px 0", width: "100%" }}
                  >
                    <MembersTableHeader />
                  </Stack>
                )}
                <SortableGroupList
                  list={unassignedList}
                  setList={setUnassignedList}
                  containerId={id}
                  isEditMode={isEditMode}
                  updatedValues={updatedValues}
                  updateHandler={updateHandler}
                  setCommitteeSelected={setCommitteeSelected}
                  setClusterSelected={setClusterSelected}
                  onAdd={onAdd}
                  onUpdate={onUpdate}
                />
              </Stack>
            )}
          </>
        )}

        {isShowMore && isNotInvolved && (
          <>
            {canLoadMore && [1, 2, 3].map((el) => <EmptyRoleRow key={el} />)}
            {showScrollTop && <ToTopBtn />}
          </>
        )}
      </Stack>
    </RowWrapper>
  );
};
